import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api.ts";
import { RelayClient, type ConnectionState } from "./relayClient.ts";

const RelayContext = createContext<RelayClient | null>(null);

/**
 * One WebSocket per tab, created after login and closed on logout/unmount. Whenever the socket closes we check
 * the session; if it ended (expiry, logout in another tab) the app returns to the login screen.
 */
export function RelayProvider({ children, onAuthLost }: { children: ReactNode; onAuthLost: () => void }) {
  const [client] = useState(() => new RelayClient());
  useEffect(() => {
    const off = client.onState((s) => {
      if (s !== "closed") return;
      api
        .session()
        .then((r) => !r.authenticated && onAuthLost())
        .catch(() => undefined);
    });
    client.connect();
    return () => {
      off();
      client.close();
    };
  }, [client, onAuthLost]);
  return <RelayContext.Provider value={client}>{children}</RelayContext.Provider>;
}

export function useRelay(): RelayClient {
  const c = useContext(RelayContext);
  if (!c) throw new Error("useRelay outside RelayProvider");
  return c;
}

export function useConnectionState(): ConnectionState {
  const relay = useRelay();
  const [state, setState] = useState(relay.connectionState);
  useEffect(() => relay.onState(setState), [relay]);
  return state;
}
